import type { ReactNode } from 'react'

interface SelectableCardProps {
  id: string
  name: string
  checked: boolean
  onSelect: () => void
  title: string
  description: string
  icon?: ReactNode
  badge?: string
}

/**
 * Radio option styled as a selectable card: radio on the left, optional icon
 * tile, bold title over a muted description. Matches the Castle UI Select Card:
 * selected cards get an ink border on a gray fill, unselected ones stay white.
 * Wrap a set of these in a fieldset with a legend.
 */
export default function SelectableCard({
  id,
  name,
  checked,
  onSelect,
  title,
  description,
  icon,
  badge,
}: SelectableCardProps) {
  return (
    <label
      htmlFor={id}
      className={`flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition-colors ${
        checked
          ? 'border-primary-ink-100 bg-primary-ink-6/60'
          : 'border-primary-ink-10 bg-white hover:border-primary-ink-30'
      }`}>
      <input
        id={id}
        type="radio"
        name={name}
        checked={checked}
        onChange={onSelect}
        // Castle UI checked radio: ink ring, white gap, large ink dot. appearance-none +
        // checked:bg-none clear the forms plugin's filled circle + white dot SVG;
        // bg-clip-content keeps the checked fill inside the 2px padding gap. The
        // checked:hover/checked:focus border repeats are required: the forms plugin's
        // :checked:hover/:checked:focus rules outrank single-variant utilities and
        // would swap in a transparent border with its blue currentColor fill
        // (text-primary-ink-100 inks currentColor as the safety net).
        className="mt-2.5 h-5 w-5 shrink-0 appearance-none rounded-full border-2 border-primary-ink-30 bg-clip-content p-[2px] text-primary-ink-100 transition-colors checked:border-primary-ink-100 checked:bg-primary-ink-100 checked:bg-none checked:hover:border-primary-ink-100 focus:ring-0 focus:ring-offset-0 checked:focus:border-primary-ink-100 focus-visible:ring-2 focus-visible:ring-primary-green-85 focus-visible:ring-offset-2"
      />
      {icon && (
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-primary-ink-10 bg-primary-ink-6 text-[#3A474E]">
          {icon}
        </span>
      )}
      {/* Castle UI Body/P2: 14/20 Semi Bold -0.15px title over 14/20 Regular -0.35px
          description (neutral-ink800 #3A474E has no marketing-palette token) */}
      <span className="min-w-0 pt-0.5">
        <span className="mb-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold leading-5 tracking-[-0.15px] text-primary-ink-100">
          {title}
          {badge && (
            <span className="inline-flex h-5 shrink-0 items-center justify-center gap-1 rounded bg-primary-ink-60 px-1 text-xs font-medium text-white">
              {badge}
            </span>
          )}
        </span>
        <span className="block text-sm leading-5 tracking-[-0.35px] text-[#3A474E]">{description}</span>
      </span>
    </label>
  )
}
